import { selOrganizationFolderTags } from '@features/st-organizations/st-organization-module'
import { BigTable } from '@st/react-util/big-table'
import { BasicUser, FolderListRow, FolderTag, TaxReturnDeliveryStatus } from '@st/sdk'
import {
  ArchiveIcon,
  ContextMenu,
  ContextMenuItem,
  EyeIcon,
  GearIcon,
  LinkIcon,
  LockClosedIcon,
  LockOpenIcon,
  TipIcon,
  SendIcon,
  TagChip,
  TimeLabel,
  Tip,
  TrashIcon,
  UnarchiveIcon
} from '@st/theme'
import { formatPercentage } from '@st/util/format'
import { SortDirection, SortState } from '@st/util/sort'
import clsx from 'clsx'
import { useMemo } from 'react'
import { match } from '@st/pattern'
import { useProcess, useSelect } from '~/store'
import { ReminderScheduleIconButton } from '../client-organizer-reminders/reminder-schedule-icon-button'
import { FolderTagsList, FolderTagsListEditButton } from '../st-folder-viewer/folder-tags-list'
import { getStatusColor, getStatusLabel } from './common'
import {
  FOLDER_DEFAULT_SORT,
  FOLDER_SORT_TRANSITIONS,
  FolderListSortColumn
} from './st-folder-list-filters-module'
import { STFolderAction, STFolderListMessage, folderDeliveryStatus } from './st-folder-list-module'
import { useOrganizationRole } from '../st-organizations/use-organization-role'
import { AssigneeCell } from './assignee-cell'

type Props = {
  items: FolderListRow[]
  organizationUsers: BasicUser[]
  selectedFolderIds: string[]
  sortState: SortState<FolderListSortColumn> | undefined
  scrollMemoryKey: string
  send: (message: STFolderListMessage) => void
  onAction: (action: STFolderAction) => void
  onToggleSort: (column: FolderListSortColumn) => void
}

export function STFolderListTable({
  items,
  organizationUsers,
  selectedFolderIds,
  sortState,
  scrollMemoryKey,
  send,
  onAction,
  onToggleSort
}: Props) {
  const organizationRole = useOrganizationRole()
  const selectedIdsSet = useMemo(() => new Set(selectedFolderIds), [selectedFolderIds])
  const allFoldersSelected = selectedIdsSet.size > 0 && items.every((f) => selectedIdsSet.has(f.id))
  const sortStateValue = sortState ?? FOLDER_DEFAULT_SORT

  const folderTags = useSelect('stOrganization', selOrganizationFolderTags)
  return (
    <BigTable
      columns={[
        { key: 'leftPadding', header: '', width: 64 },
        { key: 'select', header: '', width: 64 },
        { key: 'entities', header: 'Name' },
        { key: 'year', header: 'Tax Year', width: 90 },
        { key: 'type', header: 'Return Type', width: 90 },
        {
          key: 'clientOrganizer',
          header: (
            <div className="flex flex-row items-center gap-1">
              Client organizer{' '}
              <Tip
                title={`You can optionally send a client organizer.\nDocuments and information will automatically feed into the binder.`}
              >
                <TipIcon icon="info" />
              </Tip>
            </div>
          ),
          width: 320
        },
        { key: 'checklist', header: 'Checklist', width: 80 },
        { key: 'documents', header: 'Documents', width: 80 },
        { key: 'delivery', header: 'Delivery', width: 110 },
        { key: 'createdAt', header: 'Created at', width: 110 },
        {
          key: 'clientOrganizer.updatedAt',
          header: (
            <div className="flex flex-row items-center gap-1">
              Last activity{' '}
              <Tip
                title={`This column will be updated\nwhen a organizer is submitted\nor a new document is uploaded.`}
              >
                <TipIcon icon="info" />
              </Tip>
            </div>
          ),
          width: 110
        },
        { key: 'assignee', header: 'Assignee', width: 120 },
        { key: 'actions', header: '', width: 64 },
        { key: 'rightPadding', header: '', width: 64 }
      ]}
      rows={items}
      headerHeight={36}
      rowHeight={48}
      scrollMemoryKey={scrollMemoryKey}
      sortState={sortStateValue}
      sortOptions={{
        transitions: FOLDER_SORT_TRANSITIONS,
        defaultSort: FOLDER_DEFAULT_SORT
      }}
      renderHeaderCell={(column, { sortDirection, isSortable }) => {
        const isPaddingColumn = column.key == 'leftPadding' || column.key == 'rightPadding'

        if (column.key === 'select') {
          return (
            <div
              key={column.key}
              className="flex h-full flex-row items-center border-b border-gray-300 pl-6 pr-2"
              role="columnheader"
            >
              <input
                type="checkbox"
                aria-label="Select all binders"
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={allFoldersSelected}
                onChange={() =>
                  send({
                    type: 'setSelectedFolderIds',
                    folderIds: allFoldersSelected ? [] : items.map((item) => item.id)
                  })
                }
              />
            </div>
          )
        }

        const ariaSort = getAriaSort(isSortable, sortDirection)

        return (
          <div
            key={column.key}
            className={clsx('flex h-full flex-row items-center text-xs uppercase text-gray-600', {
              'border-b border-gray-300': !isPaddingColumn
            })}
            role={isPaddingColumn ? 'presentation' : 'columnheader'}
            aria-sort={ariaSort}
          >
            <div
              className={clsx({
                'cursor-pointer hover:text-gray-900': isSortable
              })}
              onClick={
                isSortable ? () => onToggleSort(column.key as FolderListSortColumn) : undefined
              }
            >
              {column.header}
            </div>
            {sortDirection && <SortArrow direction={sortDirection} />}
          </div>
        )
      }}
      renderRow={(folder, rowState, columns) => {
        const isSelected = selectedIdsSet.has(folder.id)
        const entityName = folder.name
        const handleRowActivate = () => {
          if (selectedIdsSet.size > 0) {
            send({ type: 'toggleFolderSelected', folderId: folder.id })
          } else {
            onAction({ type: 'openFolder', folder: folder })
          }
        }

        const hoverClasses = {
          'group-hover:bg-stone-100': !isSelected,
          'bg-blue-100 group-hover:bg-blue-200': isSelected
        }

        return (
          <div
            key={folder.id}
            aria-label={entityName ? `Open binder: ${entityName}` : 'Open binder'}
            aria-describedby={entityName ? `binder-open-hint-${folder.id}` : undefined}
            className={clsx('group h-full cursor-pointer', { 'opacity-60': folder.archived })}
            style={rowState.style}
            role="row"
            onClick={(e) => {
              if ((e.target as Element).closest('button, input')) {
                return
              }
              handleRowActivate()
            }}
          >
            {columns.map((column) => {
              const isPaddingColumn = column.key == 'leftPadding' || column.key == 'rightPadding'
              return (
                <div
                  key={column.key}
                  className={clsx(
                    'h-full',
                    {
                      'border-b border-gray-200': !isPaddingColumn
                    },
                    isPaddingColumn ? '' : hoverClasses
                  )}
                  role={isPaddingColumn ? 'presentation' : 'cell'}
                >
                  {column.key === 'select' && (
                    <SelectCell
                      isSelected={isSelected}
                      folderId={folder.id}
                      entityName={entityName}
                      send={send}
                    />
                  )}
                  {column.key === 'entities' && (
                    <EntitiesCell
                      folder={folder}
                      folderTags={folderTags}
                      onAction={onAction}
                      onOpen={handleRowActivate}
                    />
                  )}
                  {column.key === 'year' && (
                    <div className="flex h-full flex-row items-center text-base text-gray-500">
                      {folder.year}
                    </div>
                  )}
                  {column.key === 'type' && (
                    <div className="flex h-full flex-row items-center text-base text-gray-500">
                      {folder.type}
                    </div>
                  )}
                  {column.key === 'clientOrganizer' && (
                    <ClientOrganizerCell folder={folder} onAction={onAction} />
                  )}
                  {column.key === 'checklist' && (
                    <ChecklistProgressCell
                      checklistItemsCompleteCount={folder.checklistItemsCompleteCount}
                      checklistItemsTotalCount={folder.checklistItemsTotalCount}
                    />
                  )}
                  {column.key === 'documents' && <DocumentsCell folder={folder} />}
                  {column.key === 'delivery' && (
                    <DeliveryCell status={folderDeliveryStatus(folder)} />
                  )}
                  {column.key === 'createdAt' && folder.createdAt && (
                    <TimeLabel
                      className="flex h-full w-24 flex-row items-center text-base text-gray-500"
                      dateTime={folder.createdAt}
                    />
                  )}
                  {column.key === 'clientOrganizer.updatedAt' &&
                    folder.clientOrganizer.updatedAt && (
                      <TimeLabel
                        className="flex h-full w-24 flex-row items-center text-base text-gray-500"
                        dateTime={folder.clientOrganizer.updatedAt}
                      />
                    )}
                  {column.key === 'assignee' && (
                    <AssigneeCell
                      folder={folder}
                      organizationUsers={organizationUsers}
                      organizationRole={organizationRole}
                      onAction={onAction}
                    />
                  )}
                  {column.key === 'actions' && <ActionsCell folder={folder} onAction={onAction} />}
                </div>
              )
            })}
          </div>
        )
      }}
      renderEmptyMessage={() => (
        <div
          className="flex flex-row items-center"
          // actual left padding of the table (64px) + width of the checkbox column (64px) lines the text up with the name header
          style={{ height: 48, paddingLeft: 64 + 64, paddingRight: 64 }}
        >
          No binders found for the filters and/or search term you entered
        </div>
      )}
    />
  )
}

function getAriaSort(
  isSortable: boolean,
  sortDirection: 'asc' | 'desc' | undefined
): 'ascending' | 'descending' | 'none' | undefined {
  if (isSortable && sortDirection) {
    return sortDirection == 'asc' ? 'ascending' : 'descending'
  }

  if (isSortable) {
    return 'none'
  }

  return undefined
}

function SelectCell({
  isSelected,
  folderId,
  entityName,
  send
}: {
  isSelected: boolean
  folderId: string
  entityName: string | undefined
  send: (message: STFolderListMessage) => void
}) {
  return (
    <div className="flex h-full flex-row items-center pl-6 pr-2">
      <input
        type="checkbox"
        aria-label={entityName ? `Select ${entityName}` : 'Select binder'}
        className="h-4 w-4 rounded border-gray-300 text-blue-600"
        checked={isSelected}
        onChange={() => send({ type: 'toggleFolderSelected', folderId })}
      />
    </div>
  )
}

function EntitiesCell({
  folder,
  folderTags,
  onAction,
  onOpen
}: {
  folder: FolderListRow
  folderTags: FolderTag[]
  onAction: (action: STFolderAction) => void
  onOpen: () => void
}) {
  const entityName = folder.name
  return (
    <div className="flex h-full flex-row items-center gap-2">
      {entityName ? (
        <button
          type="button"
          className="overflow-hidden text-ellipsis text-nowrap text-left text-base font-semibold text-gray-900"
          aria-label={`Open binder ${entityName}`}
          data-testid={`open-binder-${folder.id}`}
          aria-describedby={`binder-open-hint-${folder.id}`}
          onClick={onOpen}
        >
          {entityName}
        </button>
      ) : (
        <div className="overflow-hidden text-ellipsis text-nowrap text-base italic text-gray-400">
          Missing name
        </div>
      )}
      {/* This meant to assist disabled people (AI agents) to understand that clicking on the row will open the binder */}
      {entityName && (
        <div id={`binder-open-hint-${folder.id}`} className="sr-only">
          Click row to open binder
        </div>
      )}
      {folder.tagIds && (
        <FolderTagsList
          leading={folder.archived && <TagChip color="gray">Archived</TagChip>}
          folderTags={folderTags}
          tagIds={folder.tagIds}
        />
      )}
      <Tip title="Edit tags">
        <FolderTagsListEditButton
          onClick={() => onAction({ type: 'editFolderTags', folder: folder })}
        />
      </Tip>
    </div>
  )
}

function DeliveryCell({ status }: { status: TaxReturnDeliveryStatus }) {
  const [label, color] = match(status)
    .with('not_delivered', () => ['Not delivered', 'gray'] as const)
    .with('delivered', () => ['Delivered', 'blue'] as const)
    .with('in_progress', () => ['In progress', 'yellow'] as const)
    .with('signed', () => ['Signed', 'green'] as const)
    .exhaustive()

  return (
    <div className="flex h-full flex-row items-center gap-2">
      <TagChip color={color} className="w-24">
        {label}
      </TagChip>
    </div>
  )
}

function ClientOrganizerCell({
  folder,
  onAction
}: {
  folder: FolderListRow
  onAction: (action: STFolderAction) => void
}) {
  const organizerSettings = useProcess('folderOrganizerSettings')
  const reminderSchedule = folder.clientOrganizer.reminderSchedule
  return (
    <div className="flex h-full flex-row items-center gap-2">
      <TagChip color={getStatusColor(folder.clientOrganizer.status)} className="w-24 shrink-0">
        {getStatusLabel(folder.clientOrganizer.status) +
          (folder.clientOrganizer.questionnaireProgress &&
          folder.clientOrganizer.status == 'in_progress'
            ? ` ${formatPercentage(folder.clientOrganizer.questionnaireProgress)}%`
            : '')}
      </TagChip>

      {folder.clientOrganizer.sent && <TagChip color="gray">Sent</TagChip>}

      {reminderSchedule && folder.clientOrganizer.status != 'submitted' && (
        <ReminderScheduleIconButton
          reminderSchedule={reminderSchedule}
          onClick={() => onAction({ type: 'viewRemindersSchedule', folder, reminderSchedule })}
        />
      )}

      <div className="invisible mx-2 flex flex-row items-center gap-2 group-hover:visible">
        <Tip title="Preview client organizer">
          <button
            aria-label="Preview client organizer"
            className="size-6"
            onClick={() => onAction({ type: 'previewQuestionnaire', folderId: folder.id })}
          >
            <EyeIcon className="size-4" />
          </button>
        </Tip>
        <Tip title="Copy shareable link">
          <button
            aria-label="Copy shareable link"
            className="size-6"
            onClick={() => onAction({ type: 'openCopyShareableLinkDialog', folder: folder })}
          >
            <LinkIcon className="size-4" />
          </button>
        </Tip>
        <Tip title="Send client organizer">
          <button
            aria-label="Send client organizer"
            className="size-6"
            onClick={() => onAction({ type: 'openSendClientOrganizerDialog', folder: folder })}
          >
            <SendIcon className="size-4" />
          </button>
        </Tip>
        <Tip title="Client organizer settings">
          <button
            aria-label="Client organizer settings"
            className="size-6"
            onClick={() =>
              organizerSettings.send({ type: 'open', folderId: folder.id, returnType: folder.type })
            }
          >
            <GearIcon className="size-4" />
          </button>
        </Tip>
        <ContextMenu aria-label="Organizer actions">
          {folder.clientOrganizer.status == 'submitted' ? (
            <ContextMenuItem
              icon={<LockOpenIcon className="size-4" />}
              onClick={() => onAction({ type: 'unlockClientOrganizer', folder: folder })}
            >
              Unsubmit client organizer
            </ContextMenuItem>
          ) : (
            <ContextMenuItem
              icon={<LockClosedIcon className="size-4" />}
              onClick={() =>
                onAction({ type: 'submitClientOrganizer', folder: folder, forceSubmit: true })
              }
            >
              Submit organizer on behalf of client
            </ContextMenuItem>
          )}
        </ContextMenu>
      </div>
    </div>
  )
}

function ActionsCell({
  folder,
  onAction
}: {
  folder: FolderListRow
  onAction: (action: STFolderAction) => void
}) {
  return (
    <div className="flex h-full flex-row items-center pr-2">
      <ContextMenu aria-label="Binder actions">
        {folder.archived == false && (
          <ContextMenuItem
            icon={<ArchiveIcon className="size-4" />}
            onClick={() => onAction({ type: 'archiveFolder', folder: folder })}
          >
            Archive binder
          </ContextMenuItem>
        )}

        {folder.archived == true && (
          <ContextMenuItem
            icon={<UnarchiveIcon className="size-4" />}
            onClick={() => onAction({ type: 'unarchiveFolder', folder: folder })}
          >
            Unarchive binder
          </ContextMenuItem>
        )}

        <ContextMenuItem
          icon={<TrashIcon className="size-4" />}
          onClick={() => onAction({ type: 'deleteFolder', folder: folder })}
        >
          Delete binder
        </ContextMenuItem>
      </ContextMenu>
    </div>
  )
}

function ChecklistProgressCell({
  checklistItemsCompleteCount,
  checklistItemsTotalCount
}: {
  checklistItemsCompleteCount: number
  checklistItemsTotalCount: number
}) {
  return (
    <div className="flex h-full flex-row items-center text-base text-gray-500">
      {checklistItemsCompleteCount} / {checklistItemsTotalCount}
    </div>
  )
}

function DocumentsCell({ folder }: { folder: FolderListRow }) {
  const unreadCount = folder.unreadDocumentsCount
  return (
    <div
      className={clsx('flex h-full flex-row items-center text-base', {
        'font-bold text-gray-900': unreadCount > 0,
        'text-gray-500': unreadCount == 0
      })}
    >
      {folder.documentsCount}
    </div>
  )
}

function SortArrow({ direction }: { direction: SortDirection }) {
  return <span className="ml-1 text-xs text-gray-500">{direction == 'asc' ? '▲' : '▼'}</span>
}
